import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import { formatDate } from "@/lib/crm";
import {
  ArrowLeft, Plus, Pencil, Trash2, Bot, Clapperboard, MapPin,
  Link as LinkIcon, Calendar, FileText, ChevronRight, Loader2,
} from "lucide-react";
import AuditionFormDialog from "@/components/crm/AuditionFormDialog";
import StageDialog from "@/components/crm/StageDialog";
import SceneDialog from "@/components/crm/SceneDialog";
import { AUDITION_STATUSES } from "@/lib/crm";

function StatusBadge({ status, t }) {
  const map = {
    new: "bg-stone-800 text-stone-300",
    in_preparation: "bg-amber-500/20 text-amber-300",
    sent: "bg-blue-500/20 text-blue-300",
    callback: "bg-fuchsia-500/20 text-fuchsia-300",
    booked: "bg-emerald-500/20 text-emerald-300",
    not_booked: "bg-rose-500/20 text-rose-300",
  };
  return (
    <span className={`mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm ${map[status] || map.new}`}>
      {t(`aud.status.${status}`)}
    </span>
  );
}

const PRE_FIELDS = ["role_fit", "result_intuition", "challenge", "training_focus", "thing_to_face"];

function PreCastingSection({ audition, t, onSaved }) {
  const initial = () => {
    const pc = audition.pre_casting || {};
    const out = {};
    for (const k of PRE_FIELDS) out[k] = pc[k] || "";
    return out;
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(initial()); /* eslint-disable-next-line */ }, [audition.audition_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/crm/auditions/${audition.audition_id}/pre-casting`, form);
      onSaved(r.data);
      toast.success(t("aud.form.saved"));
    } catch {
      toast.error(t("aud.form.fail"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="paper p-5 space-y-4" data-testid="aud-pre-casting">
      <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
        {t("aud.section.before")}
      </div>
      {PRE_FIELDS.map((k, i) => (
        <div key={k}>
          <div className="label-cap mb-2">{t(`aud.pre.q${i + 1}`)}</div>
          <textarea
            data-testid={`aud-pre-${k}`}
            value={form[k]}
            onChange={(e) => set(k, e.target.value)}
            rows={3}
            className="w-full bg-stone-950 border border-stone-800 rounded-sm p-3 text-stone-100 text-sm focus:border-amber-500/60 outline-none"
          />
        </div>
      ))}
      <div className="flex justify-end">
        <button
          type="button"
          data-testid="aud-pre-save"
          onClick={save}
          disabled={saving}
          className="pill-btn"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {t("aud.pre.save")}
        </button>
      </div>
    </section>
  );
}

function AfterAuditionSection({ audition, t, onOpen }) {
  const mr = audition.mastermind_review || {};
  const done = !!mr.completed_at;
  const rows = done
    ? [
        ["mm.summary.did_well", mr.what_worked],
        ["mm.summary.learned", mr.lesson_learned],
        ["mm.summary.pattern", mr.behavior_pattern],
        ["mm.summary.challenge", mr.current_challenge],
        ["mm.summary.next", mr.next_casting_training],
      ].filter(([, v]) => ((v || "") + "").trim())
    : [];

  return (
    <section className="paper p-5 space-y-4 border-amber-500/25" data-testid="aud-after">
      <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
        {t("aud.section.after")}
      </div>
      {!done ? (
        <>
          <h3 className="serif text-2xl text-stone-100">{t("mm.title")}</h3>
          <p className="text-stone-300 italic">{t("mm.subtitle")}</p>
          <p className="text-stone-400 text-sm whitespace-pre-line">
            {t("mm.description")}
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              data-testid="aud-mm-start"
              onClick={onOpen}
              className="pill-btn"
            >
              {t("aud.mm.start")}
            </button>
          </div>
        </>
      ) : (
        <>
          {rows.length > 0 && (
            <div className="space-y-3">
              {rows.map(([labelKey, val]) => (
                <div key={labelKey}>
                  <div className="mono text-[10px] text-amber-500 uppercase tracking-widest">
                    {t(labelKey)}
                  </div>
                  <div className="text-stone-200 whitespace-pre-line text-sm mt-1">
                    {val}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              data-testid="aud-mm-view"
              onClick={onOpen}
              className="pill-btn"
            >
              {t("aud.mm.view")}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

const SCENE_STATUS_STYLE = {
  to_prepare: "text-stone-400 border-stone-700",
  in_progress: "text-amber-400 border-amber-500/60",
  ready: "text-emerald-400 border-emerald-500/60",
};

export default function AuditionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { lang, access } = useAuth();
  const t = (k) => tr(k, lang);

  const [audition, setAudition] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const [editStage, setEditStage] = useState(null);
  const [sceneOpen, setSceneOpen] = useState(false);
  const [editScene, setEditScene] = useState(null);

  const load = async () => {
    try {
      const [a, c] = await Promise.all([
        api.get(`/crm/auditions/${id}`),
        api.get("/crm/contacts"),
      ]);
      setAudition(a.data);
      setContacts(c.data || []);
    } catch {
      toast.error("Not found");
      nav("/crm?view=auditions");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const del = async () => {
    if (!window.confirm(t("aud.confirm_delete"))) return;
    try {
      await api.delete(`/crm/auditions/${id}`);
      toast.success(t("aud.deleted"));
      nav("/crm?view=auditions");
    } catch { toast.error(t("aud.delete_fail")); }
  };

  const changeStatus = async (newStatus) => {
    try {
      const r = await api.patch(`/crm/auditions/${id}`, { status: newStatus });
      setAudition(r.data);
    } catch { toast.error(t("aud.form.fail")); }
  };

  const delStage = async (stage) => {
    if (!window.confirm(t("stage.confirm_delete"))) return;
    try {
      const r = await api.delete(`/crm/auditions/${id}/stages/${stage.stage_id}`);
      setAudition(r.data);
      toast.success(t("stage.deleted"));
    } catch { toast.error(t("stage.save_fail")); }
  };

  const delScene = async (scene) => {
    if (!window.confirm(t("asc.confirm_delete"))) return;
    try {
      await api.delete(`/crm/auditions/${id}/scenes/${scene.scene_id}`);
      toast.success(t("asc.deleted"));
      load();
    } catch { toast.error(t("asc.save_fail")); }
  };

  // Push audition context to sessionStorage and go to NewScene
  const workWithLeo = async (scene) => {
    const cd = contacts.find((c) => c.contact_id === audition.casting_director_id);
    const payload = {
      audition_id: audition.audition_id,
      audition_scene_id: scene.scene_id,
      title: `${audition.project} — ${scene.name}`,
      my_character: audition.role || "",
      project_brief: [
        audition.project && `Projekt: ${audition.project}`,
        audition.production_type && `Typ produkcji: ${audition.production_type}`,
        audition.role && `Rola: ${audition.role}`,
        cd && `Reżyser castingu: ${cd.first_name} ${cd.last_name}${cd.company ? ` (${cd.company})` : ""}`,
        cd?.notes && `Notatki o reżyserze: ${cd.notes}`,
        audition.notes && `Notatki do castingu: ${audition.notes}`,
        scene.description && `Opis sceny: ${scene.description}`,
      ].filter(Boolean).join("\n"),
      scene_text: scene.text || "",
    };
    try {
      sessionStorage.setItem("leo_audition_context", JSON.stringify(payload));
      // Mark scene as in_progress
      await api.patch(`/crm/auditions/${id}/scenes/${scene.scene_id}`, {
        status: scene.status === "to_prepare" ? "in_progress" : scene.status,
        linked_scene_id: "pending",
      });
    } catch {}
    nav("/scenes/new");
  };

  if (loading || !audition) {
    return <div className="mono text-stone-500 text-sm">{t("dash.loading")}</div>;
  }

  const cd = contacts.find((c) => c.contact_id === audition.casting_director_id);

  return (
    <div data-testid="audition-detail" className="space-y-8 max-w-5xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/crm?view=auditions" className="ghost-btn text-xs" data-testid="aud-back-btn">
          <ArrowLeft size={12} /> {t("aud.back")}
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={() => setFormOpen(true)} data-testid="aud-edit-btn" className="ghost-btn">
            <Pencil size={14} /> {t("crm.action.edit")}
          </button>
          <button
            onClick={del}
            data-testid="aud-delete-btn"
            className="ghost-btn hover:!text-rose-400 hover:!border-rose-500/50"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div>
        <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase flex items-center gap-2">
          <Clapperboard size={12} /> {t(`aud.type.${audition.audition_type}`)}
        </div>
        <h1 className="serif text-5xl text-stone-100 mt-2">{audition.project}</h1>
        {audition.role && (
          <div className="text-stone-300 mt-1 italic">{audition.role}</div>
        )}
        <div className="mt-3 flex items-center gap-3 flex-wrap text-xs text-stone-400">
          {cd && (
            <Link to={`/crm/${cd.contact_id}`} className="hover:text-amber-400">
              {t("crm.type.casting_director")}: <span className="text-stone-200">{cd.first_name} {cd.last_name}</span>
            </Link>
          )}
          <span>·</span>
          <span>
            {t("aud.next_date_label")}:{" "}
            <span className="text-stone-200">
              {audition.next_date ? formatDate(audition.next_date, lang) : t("aud.next_date_none")}
            </span>
          </span>
        </div>
      </div>

      {/* Status selector */}
      <div className="paper p-4 flex items-center gap-3 flex-wrap">
        <div className="label-cap">{t("aud.form.status")}:</div>
        {AUDITION_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => changeStatus(s)}
            data-testid={`aud-status-${s}`}
            className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
              audition.status === s
                ? "border-amber-500/70 bg-amber-500/10 text-amber-300"
                : "border-stone-800 text-stone-400 hover:border-stone-600 hover:text-stone-200"
            }`}
          >
            {t(`aud.status.${s}`)}
          </button>
        ))}
      </div>

      {(() => {
        // PRE-CASTING access: active 7 DAY FREE / start / monthly / annual.
        // Anything else (single, sprint, expired free) → minimal locked state.
        const p = access?.plan;
        let allowed = p === "start" || p === "monthly" || p === "annual" || p === "light";
        if (!allowed && p === "free") {
          const exp = access?.plan_expires_at;
          if (!exp) allowed = true;
          else {
            const d = new Date(exp);
            if (!isNaN(d) && d > new Date()) allowed = true;
          }
        }
        return allowed ? (
          <PreCastingSection audition={audition} t={t} onSaved={setAudition} />
        ) : (
          <section className="paper p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="aud-pre-locked">
            <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
              {t("aud.section.before")}
            </div>
            <button
              type="button"
              data-testid="aud-pre-unlock"
              onClick={() => nav("/career/preview/mastermind")}
              className="pill-btn"
            >
              {t("aud.mm.start")}
            </button>
          </section>
        );
      })()}
      <AfterAuditionSection audition={audition} t={t} onOpen={() => nav(`/career/mastermind-next?audition=${audition.audition_id}`)} />

      {/* Stages */}
      <section>
        <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="label-cap">{t("stage.title")}</div>
            <p className="text-xs text-stone-500 max-w-2xl mt-1">{t("stage.subtitle")}</p>
          </div>
          <button
            onClick={() => { setEditStage(null); setStageOpen(true); }}
            data-testid="stage-add-btn"
            className="pill-btn !py-2 !px-4 text-xs"
          >
            <Plus size={12} /> {t("stage.add")}
          </button>
        </div>
        {(audition.stages || []).length === 0 ? (
          <div className="paper p-6 text-stone-500 text-sm text-center">{t("stage.empty")}</div>
        ) : (
          <ul className="space-y-3">
            {audition.stages.map((s) => (
              <li key={s.stage_id} data-testid={`stage-row-${s.stage_id}`} className="paper p-4">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="mono text-[10px] uppercase tracking-widest text-amber-500">
                      {t(`aud.type.${s.kind}`)}
                    </div>
                    <StatusBadge status={s.status} t={t} />
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { setEditStage(s); setStageOpen(true); }}
                      data-testid={`stage-edit-${s.stage_id}`}
                      className="ghost-btn !py-1 !px-2 text-xs"
                    >
                      <Pencil size={11} />
                    </button>
                    <button
                      onClick={() => delStage(s)}
                      data-testid={`stage-delete-${s.stage_id}`}
                      className="ghost-btn !py-1 !px-2 text-xs hover:!text-rose-400 hover:!border-rose-500/50"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-stone-300">
                  {s.date && (
                    <div className="flex items-center gap-2">
                      <Calendar size={12} className="text-stone-500" />
                      {formatDate(s.date, lang)}
                    </div>
                  )}
                  {s.deadline && (
                    <div className="flex items-center gap-2">
                      <Calendar size={12} className="text-rose-400" />
                      Deadline: {formatDate(s.deadline, lang)}
                    </div>
                  )}
                  {s.place && (
                    <div className="flex items-center gap-2">
                      <MapPin size={12} className="text-stone-500" />
                      {s.place}{s.studio ? ` · ${s.studio}` : ""}
                    </div>
                  )}
                  {s.address && (
                    <div className="text-xs text-stone-400 col-span-2">{s.address}</div>
                  )}
                  {s.platform && (
                    <div className="text-xs text-stone-400">Platform: {s.platform}</div>
                  )}
                  {s.link && (
                    <a href={s.link} target="_blank" rel="noreferrer"
                       className="flex items-center gap-2 text-xs text-amber-400 hover:text-amber-300 truncate">
                      <LinkIcon size={12} /> {s.link}
                    </a>
                  )}
                </div>
                {(s.instructions || s.notes) && (
                  <div className="mt-2 text-xs text-stone-400 whitespace-pre-line">
                    {s.instructions}
                    {s.instructions && s.notes && "\n\n"}
                    {s.notes}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Scenes */}
      <section>
        <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
          <div className="label-cap">{t("asc.title")}</div>
          <button
            onClick={() => { setEditScene(null); setSceneOpen(true); }}
            data-testid="scene-add-btn"
            className="pill-btn !py-2 !px-4 text-xs"
          >
            <Plus size={12} />{" "}
            {(audition.scenes || []).length === 0 ? t("asc.add_first") : t("asc.add")}
          </button>
        </div>
        {(audition.scenes || []).length === 0 ? (
          <div className="paper p-6 text-stone-500 text-sm text-center">{t("asc.empty")}</div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {audition.scenes.map((s) => (
              <li key={s.scene_id} data-testid={`asc-card-${s.scene_id}`} className="paper p-4 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="serif text-xl text-stone-100">{s.name}</div>
                    <div className={`mono text-[10px] uppercase tracking-widest border px-2 py-0.5 mt-1 inline-block rounded-sm ${SCENE_STATUS_STYLE[s.status]}`}>
                      {t(`asc.status.${s.status}`)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { setEditScene(s); setSceneOpen(true); }}
                      data-testid={`asc-edit-${s.scene_id}`}
                      className="ghost-btn !py-1 !px-2 text-xs"
                    >
                      <Pencil size={11} />
                    </button>
                    <button
                      onClick={() => delScene(s)}
                      data-testid={`asc-delete-${s.scene_id}`}
                      className="ghost-btn !py-1 !px-2 text-xs hover:!text-rose-400 hover:!border-rose-500/50"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
                {s.description && (
                  <p className="text-sm text-stone-400 mt-2 line-clamp-3">{s.description}</p>
                )}
                {s.file_name && (
                  <div className="text-[11px] text-stone-500 mt-2 flex items-center gap-1">
                    <FileText size={11} /> {s.file_name}
                  </div>
                )}
                <div className="mt-auto pt-3">
                  <button
                    onClick={() => workWithLeo(s)}
                    data-testid={`asc-leo-${s.scene_id}`}
                    className="pill-btn w-full !py-2.5 text-sm"
                  >
                    <Bot size={14} /> {t("asc.work_with_leo")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Notes / Feedback */}
      {(audition.notes || audition.feedback) && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {audition.notes && (
            <div className="paper p-4">
              <div className="label-cap mb-2">{t("aud.form.notes")}</div>
              <div className="text-stone-300 text-sm whitespace-pre-line">{audition.notes}</div>
            </div>
          )}
          {audition.feedback && (
            <div className="paper p-4">
              <div className="label-cap mb-2">{t("aud.form.feedback")}</div>
              <div className="text-stone-300 text-sm whitespace-pre-line">{audition.feedback}</div>
            </div>
          )}
        </section>
      )}

      {/* Activity History */}
      <section>
        <div className="label-cap mb-3">{t("act.title")}</div>
        {(!audition.activity || audition.activity.length === 0) ? (
          <div className="paper p-4 text-stone-500 text-sm">{t("act.empty")}</div>
        ) : (
          <ol className="relative border-l border-stone-800 pl-6 space-y-4">
            {[...audition.activity].reverse().map((a, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[31px] top-1 w-6 h-6 rounded-full bg-stone-900 border border-amber-500/40 flex items-center justify-center">
                  <ChevronRight size={11} className="text-amber-400" />
                </span>
                <div className="text-xs text-stone-500 mono uppercase tracking-widest">
                  {formatDate(a.ts, lang)} · {t(`act.kind.${a.kind}`) || a.kind}
                </div>
                {a.detail && (
                  <div className="text-sm text-stone-300 mt-1">{a.detail}</div>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>

      <AuditionFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        castingDirectors={contacts.filter((c) => c.type === "casting_director")}
        audition={audition}
        onCreated={(a) => setAudition(a)}
      />
      <StageDialog
        open={stageOpen}
        onOpenChange={setStageOpen}
        auditionId={id}
        stage={editStage}
        onSaved={(a) => setAudition(a)}
      />
      <SceneDialog
        open={sceneOpen}
        onOpenChange={setSceneOpen}
        auditionId={id}
        scene={editScene}
        onSaved={(a) => setAudition(a)}
      />
    </div>
  );
}
